import { isNullOrEmpty, deepCopy } from 'src/app/core/utils/common-functions';
import { CityMaintenanceTableColumn } from './interface/city-maintenance-table-column';
import { PageInfo } from './../../../../core/models/page-info';
import { GlobalConstants } from './../../../../core/constants/global-constants.constant';
import { CommonDataService } from './../../../../core/services/common-data.service';
import { Option } from './interface/option';
import { Component, Injector, Input, OnInit, ViewChild } from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { SessionBaseComponent } from 'src/app/shared/components/session.base.component';
import { Table } from 'primeng/table';
import { CityMaintenanceTableData } from './interface/city-maintenance-table-data';
import { SortOrder } from 'src/app/core/interface/sort-order';
import { CityMaintenanceGetRequest } from './interface/city-maintenance-get-request';
import { CityMaintenanceService } from '../../service/city-maintenance.service';
import { throwError } from 'rxjs';
import { CityMaintenanceSaveRequest } from './interface/city-maintenance-save-request';
import { PermissionConstants } from 'src/app/core/constants/permission-constants.constant';

@Component({
  selector: 'laas-city-maintenance',
  templateUrl: './city-maintenance.component.html',
  styleUrls: ['./city-maintenance.component.scss']
})
export class CityMaintenanceComponent extends SessionBaseComponent implements OnInit {
  readonly viewPermissionCode = PermissionConstants.CityMaintenance;
  currentLang: string = this.laasSetting.language;
  // Status for the Query Form is open or not
  isSearchFormOpen = true;
  // The formGroup for the Query Form part
  searchFormGroup: FormGroup;

  // Options for Country dropdown list
  countryOptions: Option[] = [];
  tempCountryOptions: Option[] = [];
  // Options for Facility dropdown list
  facilityOptions: Option[] = [];

  // Status for the loading circle on the table
  isLoading: boolean = false;
  // Status for enabling a user to edit the data on the table
  isInEditMode: boolean = false;
  // The table for table part
  @ViewChild('table') table!: Table;
  // All the columns of the table have
  tableColumns: CityMaintenanceTableColumn[] = [
    { field: 'seqNo', header: 'CityMaintenance.Table.ColumnHeader.SeqNo', sort: true },
    { field: 'country', header: 'CityMaintenance.Table.ColumnHeader.Country', sort: true },
    { field: 'name', header: 'CityMaintenance.Table.ColumnHeader.Name', sort: true },
    { field: 'enName', header: 'CityMaintenance.Table.ColumnHeader.EnName', sort: true },
    { field: 'expressArea', header: 'CityMaintenance.Table.ColumnHeader.ExpressArea', sort: true },
    { field: 'createUser', header: 'CityMaintenance.Table.ColumnHeader.CreateUser', sort: true },
    { field: 'createDt', header: 'CityMaintenance.Table.ColumnHeader.CreateDt', sort: true },
    { field: 'updateUser', header: 'CityMaintenance.Table.ColumnHeader.UpdateUser', sort: true },
    { field: 'updateDt', header: 'CityMaintenance.Table.ColumnHeader.UpdateDt', sort: true }
  ];
  // The columns user choose to display
  selectedTableColumns: CityMaintenanceTableColumn[] = [];
  // Current data on the table
  tableData: CityMaintenanceTableData[] = [];
  // Backup data when data retrieved from the back-end
  backupTableData: CityMaintenanceTableData[] = [];
  // User current selected row on the table
  selectedRow: CityMaintenanceTableData | null;
  // User last selected row on the table
  previousSelectedRow: CityMaintenanceTableData | null;

  // Records for user's operation on the table
  newCityList: CityMaintenanceTableData[] = [];
  updatedCityList: CityMaintenanceTableData[] = [];
  deletedCitySeqNoList: number[] = [];

  // For data key to distinguish the added rows on the table
  newDataKeyId: number = 0;
  // Table paging info
  pageInfo: PageInfo = {
    pageSize: 5,
    pageIndex: 0,
    totalRecords: 0,
    totalPages: 0,
  };
  // Table sorting info
  sortOrder: SortOrder = {
    property: "SeqNo",
    direction: -1
  };

  constructor(
    private injector: Injector,
    private formBuilder: FormBuilder,
    private commonDataService: CommonDataService,
    private cityMaintenanceService: CityMaintenanceService
  ) {
    super(injector);
    this.searchFormGroup = this.formBuilder.group({
      country: null,
      name: null,
      enName: null,
      expressArea: null
    });

    this.commonDataService.getCountry().subscribe({
      next: (response) => {
        for (const country of response) {
          if (this.laasSetting.language === GlobalConstants.defaultLanguage) { // Zh-TW page
            this.tempCountryOptions.push({
              label: `${country.countryCode} - ${country.countryName}`,
              value: country.countryCode
            })
          }
          else { // English page
            this.tempCountryOptions.push({
              label: `${country.countryCode} - ${country.countryEnName}`,
              value: country.countryCode
            })
          }
        }
        this.countryOptions = JSON.parse(JSON.stringify(this.tempCountryOptions));
      }
    });

    this.selectedTableColumns = this.tableColumns;
    this.selectedRow = null;
    this.previousSelectedRow = this.selectedRow;
  }

  async ngOnInit(): Promise<void> {
    await super.ngOnInit();
  }

  @Input() get selectedColumns(): CityMaintenanceTableColumn[] {
    return this.selectedTableColumns;
  }

  set selectedColumns(cols: CityMaintenanceTableColumn[]) {
    // Filter display columns
    this.selectedTableColumns = this.tableColumns.filter(col => cols.includes(col));
  }

  searchBtnClick(): void {
    this.isSearchFormOpen = false;
    this.isInEditMode = false;
    this.isLoading = true;
    this.pageInfo.pageIndex = 0;
    this.table.first = 0;
    this.sendSearchRequest();
  }

  sendSearchRequest(): void {
    this.restoreData();
    const getRequest: CityMaintenanceGetRequest = {
      country: this.searchFormGroup.value.country,
      name: this.searchFormGroup.value.name,
      enName: this.searchFormGroup.value.enName,
      expressArea: this.searchFormGroup.value.expressArea,
      pageIndex: this.pageInfo.pageIndex,
      pageSize: this.pageInfo.pageSize,
      sortOrder: this.sortOrder
    }

    this.cityMaintenanceService.getCity(getRequest).subscribe({
      next: (response) => {
        this.table.cancelRowEdit(this.selectedRow);
        if (!isNullOrEmpty(response)) {

          //Update table
          if (!isNullOrEmpty(response.content)) {
            this.tableData = deepCopy(response.content)!;
            this.backupTableData = deepCopy(this.tableData)!;
            this.selectedRow = this.tableData[0];
          }
          else {
            this.tableData = [];
            this.backupTableData = [];
            this.selectedRow = null;
          }

          this.previousSelectedRow = this.selectedRow;
          this.updateTableHighlight(this.selectedRow);

          if (this.isInEditMode) {
            this.table.initRowEdit(this.selectedRow);
          }

          // Change page Info
          this.pageInfo.totalRecords = response.totalElements;
          this.pageInfo.totalPages = this.pageInfo.totalRecords / this.pageInfo.pageSize;
        }
        else {
          throwError(response);
        }
      },
      error: (response) => {
        this.tableData = [];
        this.backupTableData = [];
        this.selectedRow = null;
        this.previousSelectedRow = this.selectedRow;
        this.pageInfo.totalRecords = 0;
        this.pageInfo.totalPages = 0;
        this.toastService.error(this.translateService.instant('CityMaintenance.Toast.GetDataFail') + response.status);
      },
      complete: () => {
        this.isLoading = false;
      }
    })
  }

  addNewRow(): void {
    // Prohibit user to add a new row before user fill the required data well
    if (!this.isUserInputValid(this.previousSelectedRow)) {
      return;
    }

    // Add a new row to table's first row
    this.tableData.splice(0, 0, {
      seqNo: null, country: '', name: '', enName: '',
      createDt: new Date(), createUser: this.userService.userInfo.userName,
      updateDt: new Date(), updateUser: this.userService.userInfo.userName,
      expressArea: '', dataKey: `--${this.newDataKeyId++}`
    })

    this.clearTableEdit();
    this.table.initRowEdit(this.selectedRow);


  }

  deleteSelectedRow(): void {
    this.confirmationService.confirm({
      header: this.translateService.instant('CityMaintenance.Confirm.Header'),
      message: this.translateService.instant('CityMaintenance.Confirm.Delete'),
      icon: 'pi pi-info-circle',
      accept: () => {
        this.table.cancelRowEdit(this.selectedRow);

        if (!isNullOrEmpty(this.selectedRow)) {
          const selectedRowIndex = this.tableData.indexOf(this.selectedRow!);
          if (selectedRowIndex > -1) {
            if (!isNullOrEmpty(this.tableData[selectedRowIndex].seqNo)) {
              this.addToDeletedList(this.tableData[selectedRowIndex].seqNo!)
            }
          }

          // Remove deleted row from table
          this.tableData.splice(selectedRowIndex, 1);

          // Change selected row
          if (selectedRowIndex != this.tableData.length) {
            // Change user current selected row to the next row of the deleted row
            this.selectedRow = this.tableData[selectedRowIndex];
          }
          else { // Deleted row is the last row of the current page
            // Change user current selected row to the previous row of the deleted row
            this.selectedRow = this.tableData[selectedRowIndex - 1];
          }
          this.previousSelectedRow = this.selectedRow;
          this.updateTableHighlight(this.selectedRow);

          this.table.initRowEdit(this.selectedRow);
          this.toastService.success(this.translateService.instant("CityMaintenance.Toast.DeleteSuccess"));
        }
      }
    })
  }

  saveChanges(): void {
    this.confirmationService.confirm({
      header: this.translateService.instant('CityMaintenance.Confirm.Header'),
      message: this.translateService.instant('CityMaintenance.Confirm.Save'),
      icon: 'pi pi-info-circle',
      accept: () => {
        // Prohibit user to save row if user doesn't fill the required data on the newly added row
        if (!this.isUserInputValid(this.selectedRow)) {
          return;
        }

        this.isLoading = true;

        // Check user's operations on the table
        this.checkNewAndUpdatedRow();

        const savedData: CityMaintenanceSaveRequest = {
          newCityList: this.newCityList,
          updatedCityList: this.updatedCityList,
          deletedCitySeqNoList: this.deletedCitySeqNoList
        }

        this.cityMaintenanceService.saveCityChanges(savedData).subscribe({
          next: () => {
            this.table.cancelRowEdit(this.selectedRow);
            this.isInEditMode = false;

            this.sendSearchRequest();
            this.toastService.success(this.translateService.instant("CityMaintenance.Toast.SaveSuccess"));
          },
          error: response => {
            this.isLoading = false;
            this.newCityList = [];
            this.updatedCityList = [];
            this.toastService.error(this.translateService.instant('CityMaintenance.Toast.SaveFail') + response.status);
          }
        });
      }
    })
  }

  restoreTableDate(): void {
    this.confirmationService.confirm({
      header: this.translateService.instant('CityMaintenance.Confirm.Header'),
      message: this.translateService.instant('CityMaintenance.Confirm.Restore'),
      icon: 'pi pi-info-circle',
      accept: () => {
        this.restoreData();

        // Update table to last saved data
        this.tableData = deepCopy(this.backupTableData)!;
        this.clearTableEdit();

        this.table.initRowEdit(this.selectedRow);
        this.toastService.success(this.translateService.instant('CityMaintenance.Toast.RestoreSuccess'))
      }
    })
  }

  switchEditMode(): void {
    if (this.isInEditMode) {
      this.confirmationService.confirm({
        header: this.translateService.instant('CityMaintenance.Confirm.Header'),
        message: this.translateService.instant('CityMaintenance.Confirm.Exit'),
        icon: 'pi pi-info-circle',
        accept: () => {
          this.isInEditMode = false;
          this.restoreData();

          // Update table to last saved data
          this.tableData = deepCopy(this.backupTableData)!;
          this.clearTableEdit();

          this.toastService.success(this.translateService.instant('CityMaintenance.Toast.ExitSuccess'))
        }
      })
    }
    else {
      this.isInEditMode = true;
      if (!isNullOrEmpty(this.selectedRow)) {
        this.table.initRowEdit(this.selectedRow);
      }
    }
  }

  onRowSelect(rowData: CityMaintenanceTableData): void {
    if (this.isInEditMode) {
      // Keep the selectedRow variable is pointing at the user's selected row
      // Whether is select or deselect
      this.selectedRow = rowData;

      // Reselect after user click on the same row
      if (this.previousSelectedRow != null && this.previousSelectedRow.dataKey === this.selectedRow.dataKey) {
        this.updateTableHighlight(this.previousSelectedRow);
        return;
      }

      if (this.isUserInputValid(this.previousSelectedRow)) {
        // Change the current selected row to the user's selected row
        this.table.cancelRowEdit(this.previousSelectedRow);
        this.table.initRowEdit(this.selectedRow);
        this.previousSelectedRow = this.selectedRow;
      }
      else {
        // Rollback the selected row to the row which doesn't fill the required data well
        this.selectedRow = this.previousSelectedRow;
        this.updateTableHighlight(this.previousSelectedRow);
      }
    }
    else {
      this.previousSelectedRow = this.selectedRow;
    }
  }

  onTablePageChanging(event: { first: number, rows: number }) {
    this.pageInfo.pageSize = event.rows;
    this.pageInfo.pageIndex = event.first / event.rows;
    this.isLoading = true;
    this.sendSearchRequest();
  }

  onTableSorting(event: { field: string, order: number }) {
    if (this.tableData.length != 0) {
      this.sortOrder.property = event.field;
      this.sortOrder.direction = event.order;
      this.isLoading = true;
      this.sendSearchRequest();
    }
  }

  isNameInvalid(name: string) {
    return isNullOrEmpty(name) || name.trim().length <= 0
  }

  isEnNameInvalid(enName: string) {
    return isNullOrEmpty(enName) || enName.trim().length <= 0
  }

  isExpressAreaInvalid(expressArea: string) {
    return isNullOrEmpty(expressArea) || expressArea.trim().length <= 0
  }

  // Check if all the required data does the user filled
  private isUserInputValid(rowData: CityMaintenanceTableData | null): boolean {
    if (rowData) {
      if (
        isNullOrEmpty(rowData.country)
        || this.isNameInvalid(rowData.name)
        || this.isEnNameInvalid(rowData.enName)
        || this.isExpressAreaInvalid(rowData.expressArea)
      ) {
        this.toastService.warn(this.translateService.instant('CityMaintenance.Toast.EmptyDataError'));
        return false;
      }
    }
    return true;
  }

  // Cancel current table's row editing
  private clearTableEdit() {
    this.table.cancelRowEdit(this.selectedRow);
    this.selectedRow = this.tableData[0];
    this.previousSelectedRow = this.selectedRow;

    //Avoid the highlighted part leave on the previous row
    this.updateTableHighlight(this.selectedRow);
  }

  private restoreData(): void {
    // Clear user changed action
    this.newDataKeyId = 0;
    this.newCityList = [];
    this.updatedCityList = [];
    this.deletedCitySeqNoList = [];

    this.table.resetScrollTop();
  }

  private checkNewAndUpdatedRow(): void {
    let saveDataIndex = 0;

    if (saveDataIndex < this.tableData.length) {
      // Check if any added row by dataKey property
      // Added row's dataKey format : --id
      for (const tableData of this.tableData) {
        if (tableData.dataKey[0] != '-') {
          break;
        }
        this.addToNewAssignementDistrictList(tableData);
        saveDataIndex++;
      }

      // Reverse for maintain order of user adding new row
      this.newCityList.reverse();

      // Compare backup table data to current table data
      for (const backupData of this.backupTableData) {
        if (this.deletedCitySeqNoList.indexOf(backupData.seqNo!) == -1) {
          if (JSON.stringify(this.tableData[saveDataIndex]) != JSON.stringify(backupData)) {
            this.addToUpdateList(this.tableData[saveDataIndex]);
          }
          saveDataIndex++;
        }
      }
    }
  }

  // Control the selection highlighting back to the row
  private updateTableHighlight(currentRow: CityMaintenanceTableData | null): void {
    if (!currentRow) {
      return;
    }
    this.table.selection = currentRow;
    this.table.selectionKeys = {};
    this.table.selectionKeys[currentRow.dataKey] = 1;
    this.table.tableService.onSelectionChange();
  }

  private addToNewAssignementDistrictList(rowData: CityMaintenanceTableData): void {
    this.newCityList.push(rowData);
  }

  private addToUpdateList(rowData: CityMaintenanceTableData): void {
    rowData.updateUser = this.userService.userInfo.userName;
    rowData.updateDt = new Date();
    this.updatedCityList.push(rowData);
  }

  private addToDeletedList(seqNo: number): void {
    this.deletedCitySeqNoList.push(seqNo);
  }
}
